import { useState } from "react"
import type { ReactNode } from "react"

import { motion } from "motion/react"

import { ArrowLeftRight, ChevronRight, Star } from "@/components/icons"
import { cn } from "@/lib/utils"

import { EmptyTabText } from "./segment-detail-primitives"

export type EntityListItem = {
  description: string
  flag?: string
  title: string
  salesPotential?: "low" | "medium" | "high"
  assumedRevenue?: string
  assumedGrowth?: string
  classification?: "customer" | "prospect"
}

const SALES_POTENTIAL_STYLES: Record<string, string> = {
  high: "bg-success/15 text-success",
  medium: "bg-warning/15 text-warning-foreground",
  low: "bg-muted text-muted-foreground",
}

/**
 * FLIP wrapper for entity rows: the shared `layoutId` lets a reclassified
 * company glide between the Customers and Prospects cards (the new mount
 * animates from the old mount's bounding box), while plain `layout` slides
 * siblings to close/open the gap. The z-bump keeps the traveller painting above
 * the neighbouring card while in flight.
 */
function AnimatedRow({ children, layoutKey }: { children: ReactNode; layoutKey: string }) {
  const [inFlight, setInFlight] = useState(false)
  return (
    <motion.div
      layout
      className={cn("relative", inFlight && "z-10")}
      layoutId={layoutKey}
      transition={{ layout: { type: "spring", stiffness: 350, damping: 32 } }}
      onLayoutAnimationComplete={() => setInFlight(false)}
      onLayoutAnimationStart={() => setInFlight(true)}
    >
      {children}
    </motion.div>
  )
}

export function EntityList({
  animateLayout,
  ctaLabel = "Dossier",
  ctaTestId,
  emptyText,
  isFavourite,
  items,
  onAnalyze,
  onToggleFavourite,
  onReclassify,
}: {
  /** FLIP-animate row moves (reclassify glide); needs a `LayoutGroup` ancestor. */
  animateLayout?: boolean
  /** Row CTA text — "Dossier" for customers (→ CCP), "Competitor plan" for competitors. */
  ctaLabel?: string
  /** data-testid for the row CTA button (dossier / competitor-plan entry points). */
  ctaTestId?: string
  emptyText?: string
  isFavourite?: (companyName: string) => boolean
  items: EntityListItem[]
  /** Opens the row's plan/dossier — semantics depend on which tab renders the list. */
  onAnalyze?: (companyName: string) => void
  onToggleFavourite?: (companyName: string) => void
  /** Reclassify customer↔prospect (edit-gated); omit to hide the control. */
  onReclassify?: (companyName: string, next: "customer" | "prospect") => void
}) {
  if (items.length === 0) {
    return <EmptyTabText>{emptyText ?? "No matching companies."}</EmptyTabText>
  }
  return (
    <div className="grid gap-3">
      {items.map((item) => {
        const favourited = isFavourite?.(item.title) ?? false
        const star = onToggleFavourite ? (
          <button
            aria-label={favourited ? `Unfavourite ${item.title}` : `Favourite ${item.title}`}
            aria-pressed={favourited}
            type="button"
            className={cn(
              "focus-visible:ring-ring absolute top-2 right-2 inline-flex size-7 items-center justify-center rounded-md transition focus-visible:ring-2 focus-visible:outline-none",
              favourited
                ? "text-warning-ink"
                : "text-muted-foreground/50 hover:bg-accent hover:text-warning-ink"
            )}
            onClick={(event) => {
              event.stopPropagation()
              onToggleFavourite(item.title)
            }}
          >
            <Star className={cn("size-4", favourited && "fill-current")} />
          </button>
        ) : null
        const nextClassification: "customer" | "prospect" =
          item.classification === "customer" ? "prospect" : "customer"
        const reclassify =
          onReclassify && item.classification ? (
            <button
              aria-label={`Reclassify ${item.title} as ${nextClassification}`}
              title={`Mark as ${nextClassification}`}
              type="button"
              className={cn(
                "text-muted-foreground/50 hover:bg-accent hover:text-primary focus-visible:ring-ring absolute top-2 inline-flex size-7 items-center justify-center rounded-md transition focus-visible:ring-2 focus-visible:outline-none",
                onToggleFavourite ? "right-9" : "right-2"
              )}
              onClick={(event) => {
                event.stopPropagation()
                onReclassify(item.title, nextClassification)
              }}
            >
              <ArrowLeftRight className="size-4" />
            </button>
          ) : null
        const body = (
          <>
            <div className="bg-primary/10 text-primary flex size-9 shrink-0 items-center justify-center rounded-full text-xs font-bold">
              {initials(item.title)}
            </div>
            <div className="min-w-0 flex-1">
              <h4 className="text-foreground flex flex-wrap items-center gap-2 text-sm font-semibold">
                {item.title}
                {item.classification ? (
                  // Keyed by classification so the new badge pops in when a row
                  // is reclassified (the row remounts in the other section, so
                  // an exit animation would never be visible — entry is enough).
                  <motion.span
                    key={item.classification}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-muted text-muted-foreground rounded-md px-1.5 py-0.5 text-[10px] font-semibold capitalize"
                    initial={animateLayout ? { opacity: 0, scale: 0.8 } : false}
                    transition={{ duration: 0.18 }}
                  >
                    {item.classification}
                  </motion.span>
                ) : null}
                {item.flag && (
                  <span className="bg-primary/10 text-primary rounded-md px-2 py-0.5 text-[10px] font-semibold">
                    {item.flag}
                  </span>
                )}
              </h4>
              <p className="text-muted-foreground mt-1 text-sm">{item.description}</p>
              {item.salesPotential || item.assumedRevenue || item.assumedGrowth ? (
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                  {item.salesPotential ? (
                    <span
                      className={cn(
                        "rounded-md px-1.5 py-0.5 font-semibold",
                        SALES_POTENTIAL_STYLES[item.salesPotential]
                      )}
                    >
                      {item.salesPotential} potential
                    </span>
                  ) : null}
                  {item.assumedRevenue ? (
                    <span className="text-muted-foreground">Rev {item.assumedRevenue}</span>
                  ) : null}
                  {item.assumedGrowth ? (
                    <span className="text-muted-foreground">Growth {item.assumedGrowth}</span>
                  ) : null}
                </div>
              ) : null}
            </div>
            {/* Ghost pill (§ #2063) — reads as a real button but stays quiet;
                fills softly when the row is hovered. */}
            {onAnalyze ? (
              <span className="border-primary/30 text-primary group-hover:border-primary/50 group-hover:bg-primary/10 mt-0.5 inline-flex shrink-0 items-center gap-0.5 rounded-md border px-2 py-0.5 text-xs font-semibold transition-colors">
                {ctaLabel}
                <ChevronRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
              </span>
            ) : null}
          </>
        )

        const controlPad = star && reclassify ? "pr-16" : "pr-10"
        // The motion wrapper (not the row itself) carries the FLIP transform so
        // card-interactive's CSS hover-translate never fights the spring.
        const row = !onAnalyze ? (
          <article
            className={cn("bg-muted/50 relative flex items-start gap-3 rounded-md p-3", controlPad)}
          >
            {body}
            {reclassify}
            {star}
          </article>
        ) : (
          <div
            role="presentation"
            className={cn(
              "card-interactive bg-muted/50 hover:bg-muted group relative flex w-full items-start gap-3 rounded-md p-3 text-left transition",
              controlPad
            )}
            // Mouse convenience for the whole row; keyboard users activate the inner button.
            onClick={() => onAnalyze(item.title)}
          >
            <button
              aria-label={`${ctaLabel} — ${item.title}`}
              className="focus-visible:ring-ring flex min-w-0 flex-1 items-start gap-3 rounded-sm text-left focus-visible:ring-2 focus-visible:outline-none"
              data-testid={ctaTestId}
              type="button"
            >
              {body}
            </button>
            {reclassify}
            {star}
          </div>
        )

        return animateLayout ? (
          <AnimatedRow key={item.title} layoutKey={`company-${item.title}`}>
            {row}
          </AnimatedRow>
        ) : (
          <div key={item.title}>{row}</div>
        )
      })}
    </div>
  )
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((word) => word[0])
    .slice(0, 2)
    .join("")
    .toUpperCase()
}
